import { createContext, useRef } from 'react';
import useImage from '../hooks/useImage';
import { DancerPathNodeShape } from '../models/Timeline';
import { createTintedImageCanvas } from '../utils/canvasDrawing';

interface TintedImage {
	canvas: HTMLCanvasElement;
	dataUrl: string;
}

type ImagesContextType = {
	images: {
		audience: HTMLImageElement | null;
		triangleNode: HTMLImageElement | null;
		squareNode: HTMLImageElement | null;
		heartNode: HTMLImageElement | null;
		tearNode: HTMLImageElement | null;
		alphaNode: HTMLImageElement | null;
		omegaNode: HTMLImageElement | null;
	};
	getPathNodeImage: (pathNodeShape: DancerPathNodeShape) => HTMLImageElement | null;
	getTintedImage: (image: HTMLImageElement | null, color: string) => TintedImage | null;
};

export const ImagesContext = createContext<ImagesContextType>({
	images: {
		audience: null,
		triangleNode: null,
		squareNode: null,
		heartNode: null,
		tearNode: null,
		alphaNode: null,
		omegaNode: null,
	},
	getPathNodeImage: (pathNodeShape: DancerPathNodeShape) => null,
	getTintedImage: (image: HTMLImageElement | null, color: string) => null,
});

export const ImagesContextStore = (props: any) => {
	const images = {
		audience: useImage('./DPL_Icons/DPL_Audience.png'),
		triangleNode: useImage('./DPL_Icons/DPL_Icon_NodeTriangle.png'),
		squareNode: useImage('./DPL_Icons/DPL_Icon_NodeSquare.png'),
		heartNode: useImage('./DPL_Icons/DPL_Icon_NodeHeart.png'),
		tearNode: useImage('./DPL_Icons/DPL_Icon_NodeTear.png'),
		alphaNode: useImage('./DPL_Icons/DPL_Icon_NodeAlpha.png'),
		omegaNode: useImage('./DPL_Icons/DPL_Icon_NodeOmega.png'),
	};

	const getPathNodeImage = (pathNodeShape: DancerPathNodeShape) => {
		switch (pathNodeShape) {
			case DancerPathNodeShape.Triangle: {
				return images.triangleNode;
			}
			case DancerPathNodeShape.Heart: {
				return images.heartNode;
			}
			case DancerPathNodeShape.Square: {
				return images.squareNode;
			}
			case DancerPathNodeShape.Tear: {
				return images.tearNode;
			}
		}
	};

	const cachedTintedImages = useRef(
		new Map<HTMLImageElement, Map<string, { canvas: HTMLCanvasElement; dataUrl: string }>>()
	);

	const getTintedImage = (image: HTMLImageElement | null, color: string) => {
		if (image === null) return null;

		const cachedTintedImage = cachedTintedImages.current.get(image)?.get(color);
		if (cachedTintedImage !== undefined) return cachedTintedImage;

		const canvas = createTintedImageCanvas(image, color);
		if (canvas === null) return null;

		const dataUrl = canvas.toDataURL();

		if (!cachedTintedImages.current.has(image)) {
			cachedTintedImages.current.set(image, new Map());
		}

		const tintedImage = { canvas: canvas, dataUrl: dataUrl };
		cachedTintedImages.current.get(image)?.set(color, tintedImage);
		return tintedImage;
	};

	return (
		<ImagesContext.Provider
			value={{
				images,
				getPathNodeImage,
				getTintedImage,
			}}
		>
			{props.children}
		</ImagesContext.Provider>
	);
};
